/**
 * v1 booking / event wire → {@link BookingBilling} domain.
 * Import only from `*.http.ts` or other API ingress modules.
 */

import { maybe } from '@passionware/monads';
import {
  fromAbsolute,
  parseAbsolute,
  parseDate,
  parseTime
} from '@internationalized/date';
import type {
  BookingBilling,
  BookingRateInterval,
  BookingResourceRate,
  DailyHours,
  WorkSchedule
} from 'v5/core/booking-billing/model';
import type { ExpenseLine } from 'v5/core/expense-billing/model';
import type {
  EvidenceDocumentKind,
  FinancialDocument
} from 'v5/core/financial-reconciliation/model';
import { evidenceDocumentStatusFromWire } from 'v5/core/financial-reconciliation/status';
import type { CurrencyCode } from 'v5/core/money/model';

/** Legacy v1 {@link Event} fields used at the HTTP boundary. */
export type BookingEventFields = {
  date_type?: string;
  start_date?: string | Date;
  end_date?: string | Date;
  start_timestamp?: string | Date;
  end_timestamp?: string | Date;
  all_day_duration_hours?: number | null;
  estimated_daily_hours?: number | null;
  estimated_daily_minutes?: number | null;
  estimated_start_time?: string | null;
  estimated_end_time?: string | null;
  include_weekends?: boolean | null;
};

export type BookingRateFields = {
  rate_currency?: string | null;
  rate_amount?: number | null;
  rate_interval?: string | null;
  rate_agency_fee?: number | null;
  rate_payroll_fee?: number | null;
  rate_processing_fee?: number | null;
};

export type BookingInvoiceFields = {
  id: number | string;
  archived?: boolean;
  status?: string;
  document_data?: { amount?: number };
};

export function financialDocumentFromV1Wire(
  doc: BookingInvoiceFields,
  kind: EvidenceDocumentKind,
  currency: CurrencyCode
): FinancialDocument {
  return {
    id: String(doc.id),
    kind,
    status: evidenceDocumentStatusFromWire(doc.status),
    amount: {
      amount: doc.document_data?.amount ?? 0,
      currency
    },
    archived: Boolean(doc.archived)
  };
}

export type BookingBillingSourceFields = BookingRateFields & {
  id: number | string;
  booking_type?: { include_weekends_default?: boolean | null };
  events?: BookingEventFields[] | null;
  invoices?: BookingInvoiceFields[] | null;
};

function parseIntervalString(
  interval: string | null | undefined
): BookingRateInterval {
  if (interval === 'HOURLY' || interval === 'hourly') return 'hourly';
  if (interval === 'FIXED' || interval === 'fixed') return 'fixed';
  return 'daily';
}

function calendarDateString(
  value: DateString | Date | undefined
): string | null {
  if (value == null) return null;
  if (value instanceof Date) {
    return value.toISOString().slice(0, 10);
  }
  return String(value).slice(0, 10);
}

function timeString(
  value: BookingEventFields['estimated_start_time']
): string | null {
  if (value == null) return null;
  return String(value).slice(0, 8);
}

function timestampToZoned(
  value: string | Date
): ReturnType<typeof parseAbsolute> {
  if (value instanceof Date) {
    return fromAbsolute(value.getTime(), 'UTC');
  }
  const asString = String(value);
  if (/^\d+$/.test(asString)) {
    return fromAbsolute(Number(asString), 'UTC');
  }
  const iso = /[zZ]|[+-]\d{2}:?\d{2}$/.test(asString)
    ? asString
    : `${asString}Z`;
  return parseAbsolute(iso, 'UTC');
}

export const bookingBillingFromV1 = {
  workScheduleFromEvent(
    event: BookingEventFields,
    includeWeekendsDefault: boolean
  ): WorkSchedule | null {
    const dateType = event.date_type;
    const includeWeekends =
      event.include_weekends ?? includeWeekendsDefault ?? false;

    if (dateType === 'INCLUSIVE_HOURS') {
      if (!event.start_timestamp || !event.end_timestamp) {
        return null;
      }
      return {
        kind: 'continuous',
        range: {
          from: timestampToZoned(event.start_timestamp),
          to: timestampToZoned(event.end_timestamp)
        }
      };
    }

    const startDate = calendarDateString(event.start_date);
    const endDate = calendarDateString(event.end_date);
    if (!startDate || !endDate) {
      return null;
    }

    let dailyHours: DailyHours;
    if (dateType === 'RECURRING_HOURS') {
      dailyHours = {
        kind: 'estimated',
        hours: event.estimated_daily_hours ?? 0,
        minutes: event.estimated_daily_minutes ?? 0
      };
    } else if (dateType === 'RECURRING_SPECIFIC_HOURS') {
      const startTime = timeString(event.estimated_start_time);
      const endTime = timeString(event.estimated_end_time);
      if (!startTime || !endTime) {
        return null;
      }
      dailyHours = {
        kind: 'window',
        start: parseTime(startTime),
        end: parseTime(endTime)
      };
    } else {
      dailyHours = {
        kind: 'allDay',
        hoursPerDay: event.all_day_duration_hours ?? 0
      };
    }

    return {
      kind: 'recurring',
      range: {
        from: parseDate(startDate),
        to: parseDate(endDate),
        includeWeekends: Boolean(includeWeekends)
      },
      dailyHours
    };
  },

  workScheduleToEventFields(schedule: WorkSchedule): BookingEventFields | null {
    if (schedule.kind === 'continuous') {
      return {
        date_type: 'INCLUSIVE_HOURS',
        start_timestamp: schedule.range.from.toDate(),
        end_timestamp: schedule.range.to.toDate()
      };
    }

    const { range, dailyHours } = schedule;
    const base: BookingEventFields = {
      start_date: range.from.toString(),
      end_date: range.to.toString(),
      include_weekends: range.includeWeekends
    };

    if (dailyHours.kind === 'estimated') {
      return {
        ...base,
        date_type: 'RECURRING_HOURS',
        estimated_daily_hours: dailyHours.hours,
        estimated_daily_minutes: dailyHours.minutes
      };
    }

    if (dailyHours.kind === 'window') {
      const pad = (n: number) => String(n).padStart(2, '0');
      return {
        ...base,
        date_type: 'RECURRING_SPECIFIC_HOURS',
        estimated_start_time: `${pad(dailyHours.start.hour)}:${pad(dailyHours.start.minute)}:${pad(dailyHours.start.second)}`,
        estimated_end_time: `${pad(dailyHours.end.hour)}:${pad(dailyHours.end.minute)}:${pad(dailyHours.end.second)}`
      };
    }

    return {
      ...base,
      date_type: 'RECURRING_DEFAULT',
      all_day_duration_hours: dailyHours.hoursPerDay
    };
  },

  rateFromBookingFields(booking: BookingRateFields): BookingResourceRate {
    const hasFees =
      booking.rate_agency_fee != null ||
      booking.rate_payroll_fee != null ||
      booking.rate_processing_fee != null;

    return {
      currency: booking.rate_currency ?? 'USD',
      amountPerUnit: booking.rate_amount ?? 0,
      interval: parseIntervalString(booking.rate_interval ?? undefined),
      fees: hasFees
        ? maybe.of({
            agency: booking.rate_agency_fee ?? 0,
            payroll: booking.rate_payroll_fee ?? 0,
            processing: booking.rate_processing_fee ?? 0
          })
        : maybe.ofAbsent()
    };
  },

  resolveEventsForBilling(
    booking: Pick<BookingBillingSourceFields, 'events'>,
    assignmentEvents: BookingEventFields[]
  ): BookingEventFields[] {
    const bookingEvents = booking.events ?? [];
    const fallbackHours = 8;

    if (assignmentEvents.length > 0) {
      return assignmentEvents.map((event, index) => ({
        ...event,
        all_day_duration_hours:
          event.all_day_duration_hours ??
          bookingEvents[index]?.all_day_duration_hours ??
          bookingEvents[0]?.all_day_duration_hours ??
          fallbackHours
      }));
    }

    if (bookingEvents.length > 0) {
      return bookingEvents.map(event => ({
        ...event,
        all_day_duration_hours: event.all_day_duration_hours ?? fallbackHours
      }));
    }

    return [];
  },

  billingFromBookingFields(
    booking: BookingBillingSourceFields,
    assignmentEvents: BookingEventFields[],
    expenses: ExpenseLine[] = []
  ): BookingBilling {
    const includeWeekendsDefault =
      booking.booking_type?.include_weekends_default ?? false;
    const eventsForBilling = bookingBillingFromV1.resolveEventsForBilling(
      booking,
      assignmentEvents
    );
    const schedules = eventsForBilling
      .map(e =>
        bookingBillingFromV1.workScheduleFromEvent(e, includeWeekendsDefault)
      )
      .filter((s): s is WorkSchedule => s != null);

    const currency = (booking.rate_currency ?? 'USD') as CurrencyCode;
    const invoiceDocuments = (booking.invoices ?? []).map(inv =>
      financialDocumentFromV1Wire(inv, 'invoice', currency)
    );
    const receiptDocuments = expenses.flatMap(exp =>
      exp.archived ? [] : exp.documents
    );
    const documents: BookingBilling['documents'] = [
      ...invoiceDocuments,
      ...receiptDocuments
    ];

    return {
      bookingId: String(booking.id),
      rate: bookingBillingFromV1.rateFromBookingFields(booking),
      schedules,
      expenses,
      documents
    };
  }
} as const;
